/**
 * Dead-end sidings: a real 3-way `split` piece IN the ring (its main channel
 * is the straight the window realized; the third port opens sideways), with
 * a short branch — straight-hole, then an end cap — dead-ending beside the
 * track. The marble keeps to the main line (the loop centerline is
 * unchanged); the branch is scenery with honest junction geometry. Branch
 * marbles would roll in and drop through the hole — a drain spur.
 *
 * The native split branches LEFT of the flow; a right-hand branch renders
 * the same mesh mirrored.
 */
import type { Dir, Placement } from "../shared/types.ts";
import type { Rng } from "./rng.ts";
import { DIR_STEP } from "./geom.ts";

const STUB_CHANCE = 0.45;

const CAPS = [
  "end-hole-rounded",
  "end-hole-square",
  "end-rounded",
  "end-square",
];

export interface StubPlan {
  /** New branch placements (the chosen straights are mutated in place). */
  placements: Placement[];
  /** Cells the branches occupy (world "x,z" keys) — merge into trackCells. */
  cells: string[];
}

export function planStubs(
  rng: Rng,
  /** All assembled placements — chosen `straight`s become `split`s. */
  placements: Placement[],
  /** Full track footprint incl. supports (assembled). */
  occupied: ReadonlySet<string>,
): StubPlan {
  const empty: StubPlan = { placements: [], cells: [] };
  if (!rng.chance(STUB_CHANCE)) return empty;

  const key = (x: number, z: number) => `${x},${z}`;
  // Cells that carry a tunnel arch — a junction under a tunnel reads wrong.
  const arched = new Set(
    placements
      .filter((p) => p.pieceId === "tunnel")
      .map((p) => key(Math.round(p.pos[0]), Math.round(p.pos[2]))),
  );
  // The goal gate straddles its straight — keep the lap line clean.
  const goalCells = new Set(
    placements
      .filter((p) => p.goal)
      .map((p) => key(Math.round(p.pos[0]), Math.round(p.pos[2]))),
  );
  // Cells carrying stacked track (bridge decks over channels): a junction
  // in the lower channel would sit under the bridge — skip.
  const cellCounts = new Map<string, number>();
  for (const p of placements) {
    if (p.role !== "track") continue;
    const k = key(Math.round(p.pos[0]), Math.round(p.pos[2]));
    cellCounts.set(k, (cellCounts.get(k) ?? 0) + 1);
  }
  const stacked = (k: string) => (cellCounts.get(k) ?? 0) > 1;

  /** Rotate a mesh-local offset by the placement rotation (r quarters). */
  const rot = (r: Dir, x: number, z: number): [number, number] =>
    r === 0 ? [x, z] : r === 1 ? [z, -x] : r === 2 ? [-x, -z] : [-z, x];

  interface Candidate {
    placement: Placement;
    /** What the host placement becomes (junction id; mirrored optional). */
    junction: string;
    mirrored?: boolean;
    branch: [number, number][]; // cells outward from the ring
    dir: Dir; // branch direction (away from the ring)
  }
  const taken = new Set<string>(occupied);
  const candidates: Candidate[] = [];
  for (const p of placements) {
    if (p.role !== "track" || p.pos[1] !== 0) continue;

    if (p.pieceId === "straight") {
      const cx = Math.round(p.pos[0]);
      const cz = Math.round(p.pos[2]);
      if (
        arched.has(key(cx, cz)) || goalCells.has(key(cx, cz)) ||
        stacked(key(cx, cz))
      ) continue;
      // Placement rotation r maps entry-frame +z to the flow direction.
      const flow = ((1 - p.rotation + 4) % 4) as Dir;
      for (const side of [1, 3] as const) {
        const w = ((flow + side) % 4) as Dir;
        const [dx, dz] = DIR_STEP[w];
        const s1: [number, number] = [cx + dx, cz + dz];
        const s2: [number, number] = [cx + dx * 2, cz + dz * 2];
        if (taken.has(key(...s1)) || taken.has(key(...s2))) continue;
        candidates.push({
          placement: p,
          // side 1 = turnLeft(flow) — the native split branches left; a
          // right branch renders the same mesh mirrored.
          junction: "split",
          mirrored: side === 3,
          branch: [s1, s2],
          dir: w,
        });
      }
    }

    // Turn junctions: split-left is the curve mesh plus a branch port on
    // the outer flank (measured — drop-in replacement); split-right is its
    // native mirror, so rendering it mirrored gives the same shape while
    // covering the right-handed id. Same for the large family.
    const turn = p.pieceId === "curve" || p.pieceId === "curve-solid"
      ? { large: false, solid: p.pieceId === "curve-solid" }
      : p.pieceId === "curve-large" || p.pieceId === "curve-solid-large"
      ? { large: true, solid: p.pieceId === "curve-solid-large" }
      : null;
    if (turn && !p.mirrored) {
      // Branch continues along mesh +x from the outer flank cell.
      const cell0 = turn.large ? [2, 1] : [1.5, 0.5];
      const w = ((0 - p.rotation + 4) % 4) as Dir;
      const b: [number, number][] = [];
      let ok = true;
      for (let j = 0; j < 2; j++) {
        const [ox, oz] = rot(p.rotation, cell0[0] + j, cell0[1]);
        const bx = Math.round(p.pos[0] + ox);
        const bz = Math.round(p.pos[2] + oz);
        if (taken.has(key(bx, bz))) ok = false;
        b.push([bx, bz]);
      }
      if (ok) {
        candidates.push({
          placement: p,
          junction: "", // resolved at placement time (left/right alternation)
          branch: b,
          dir: w,
        });
      }
    }
  }
  if (candidates.length === 0) return empty;

  const count = rng.int(1, 3);
  let placed = 0;
  const usedHosts = new Set<Placement>();
  const plan: StubPlan = { placements: [], cells: [] };
  for (const cand of rng.shuffle(candidates)) {
    if (placed >= count) break;
    if (usedHosts.has(cand.placement)) continue;
    if (cand.branch.some(([x, z]) => taken.has(key(x, z)))) continue;

    // The host becomes the junction (same cell, same rotation — the
    // junction's main channel is the host's channel).
    if (cand.junction === "split") {
      cand.placement.pieceId = "split";
      if (cand.mirrored) cand.placement.mirrored = true;
    } else {
      const large = cand.placement.pieceId.includes("large");
      const solid = cand.placement.pieceId.includes("solid");
      // Alternate ids: the right-handed GLB mirrored renders the same
      // left-handed shape (s-curve convention), keeping both ids in play.
      const right = rng.chance(0.5);
      cand.placement.pieceId = [
        "split",
        ...(large ? ["large"] : []),
        ...(solid ? ["solid"] : []),
        right ? "right" : "left",
      ].join("-");
      if (right) cand.placement.mirrored = true;
    }
    usedHosts.add(cand.placement);

    // TEE-SPUR (M13): the branch stems into a split-double-sides — a
    // T-junction with NO through port (unusable on the ring, which kept
    // it coverage-exempt until now) whose two arms dead-end in caps. A
    // 3×2 footprint + 2 cap cells must be free; rarity is honest.
    const teePlaced = cand.junction === "split" && rng.chance(0.3) &&
      (() => {
        const w = cand.dir;
        const rT = ((1 - w + 4) % 4) as Dir;
        const [s1x, s1z] = cand.branch[0];
        const cellAt = (ex: number, ez: number): [number, number] => {
          const [ox, oz] = rot(rT, ex, ez);
          return [s1x + ox, s1z + oz];
        };
        const footprint: [number, number][] = [];
        for (const ez of [0, 1]) {
          for (const ex of [-1, 0, 1]) footprint.push(cellAt(ex, ez));
        }
        const capA = cellAt(2, 1); // arm exit toward mesh +x
        const capB = cellAt(-2, 1);
        const all = [...footprint, capA, capB];
        if (all.some(([x, z]) => taken.has(key(x, z)))) return false;
        const [mx, mz] = rot(rT, 0, 0.5);
        plan.placements.push({
          pieceId: "split-double-sides",
          pos: [s1x + mx, 0, s1z + mz],
          rotation: rT,
          role: "track",
        });
        const dirOf = (dx: number, dz: number): Dir =>
          dx === 1 ? 0 : dz === 1 ? 1 : dx === -1 ? 2 : 3;
        for (const [capCell, ex] of [[capA, 1], [capB, -1]] as const) {
          const [adx, adz] = rot(rT, ex, 0);
          const armDir = dirOf(adx, adz);
          plan.placements.push({
            pieceId: rng.pick(["end-rounded", "end-square"]),
            pos: [capCell[0], 0, capCell[1]],
            rotation: ((1 - armDir + 4) % 4) as Dir,
            role: "track",
          });
        }
        for (const [x, z] of all) {
          taken.add(key(x, z));
          plan.cells.push(key(x, z));
        }
        return true;
      })();
    if (teePlaced) {
      placed++;
      continue;
    }

    const len = rng.chance(0.6) ? 2 : 1;
    const rotation = ((1 - cand.dir + 4) % 4) as Dir;
    const cap = rng.pick(CAPS);
    const pieces = len === 2 ? ["straight-hole", cap] : [cap];
    for (let j = 0; j < len; j++) {
      const [x, z] = cand.branch[j];
      plan.placements.push({
        pieceId: pieces[j],
        pos: [x, 0, z],
        rotation,
        role: "track",
      });
      taken.add(key(x, z));
      plan.cells.push(key(x, z));
    }
    // An unused branch cell stays free for decor when the spur is short.
    placed++;
  }

  // STUB GARDEN (M19c): a clustered ensemble — 2-3 sidings on nearby
  // hosts sharing ONE cap style, with a trackside fan beyond the middle
  // stub's cap facing back at the ring (M19a loop wind: its capsule
  // brushes the main channel, so the garden literally breathes on
  // passing marbles). Own child stream, appended after the singles pass.
  const gRng = rng.child("garden");
  if (gRng.chance(0.35)) {
    const fresh = candidates.filter((c) =>
      !usedHosts.has(c.placement) &&
      c.junction === "split" &&
      !c.branch.some(([x, z]) => taken.has(key(x, z)))
    );
    // Cluster: seed candidate + others whose hosts are within 3 cells.
    for (const seedCand of gRng.shuffle(fresh)) {
      const [sx, sz] = [
        Math.round(seedCand.placement.pos[0]),
        Math.round(seedCand.placement.pos[2]),
      ];
      const cluster = [seedCand];
      for (const c of fresh) {
        if (c === seedCand || cluster.length >= 3) continue;
        if (usedHosts.has(c.placement) || cluster.some((m) => m.placement === c.placement)) continue;
        const dx = Math.abs(Math.round(c.placement.pos[0]) - sx);
        const dz = Math.abs(Math.round(c.placement.pos[2]) - sz);
        if (Math.max(dx, dz) <= 3) cluster.push(c);
      }
      if (cluster.length < 2) continue;
      const cap = gRng.pick(["end-rounded", "end-square"]);
      let ok = 0;
      for (const cand of cluster) {
        const [bx, bz] = cand.branch[0];
        if (taken.has(key(bx, bz))) continue;
        cand.placement.pieceId = "split";
        if (cand.mirrored) cand.placement.mirrored = true;
        usedHosts.add(cand.placement);
        plan.placements.push({
          pieceId: cap,
          pos: [bx, 0, bz],
          rotation: ((1 - cand.dir + 4) % 4) as Dir,
          role: "track",
        });
        taken.add(key(bx, bz));
        plan.cells.push(key(bx, bz));
        ok++;
      }
      if (ok >= 2) {
        // The garden fan: one cell past the middle stub's cap, blades
        // facing the ring (rotation maps local +z toward −dir).
        const mid = cluster[Math.floor(cluster.length / 2)];
        const [fx, fz] = DIR_STEP[mid.dir];
        const [bx, bz] = mid.branch[0];
        const fanCell: [number, number] = [bx + fx, bz + fz];
        if (!taken.has(key(...fanCell))) {
          const facing = ((mid.dir + 2) % 4) as Dir;
          plan.placements.push({
            pieceId: gRng.pick([
              "fan-standing-two",
              "fan-standing-four",
              "fan-two",
              "fan-four",
            ]),
            pos: [fanCell[0], 0, fanCell[1]],
            rotation: ((1 - facing + 4) % 4) as Dir,
            role: "track",
            spin: { axis: [0, 0, 1], radPerSec: 4 + gRng.float() * 3 },
          });
          taken.add(key(...fanCell));
          plan.cells.push(key(...fanCell));
        }
      }
      break;
    }
  }
  return plan;
}
